<template>
  <div>
    <div class="modal-content px20 pb10 pt15 gdpr-modal-content">
      <div
        v-for="cookieSetupData in gdprCookieSetupData"
        :key="cookieSetupData.groupId"
        class="border-top"
      >
        <div class="cookie-name">
          <h4 class="inline-block gdpr-cookie-group-title fs24 mr5 mt15 mb15">
            {{ $t(cookieSetupData.name) }}
          </h4>
          <label
            class="switch mb5"
            :class="{ opacity2: cookieSetupData.isEssential }"
          >
            <input
              v-model="cookieGroupIds"
              type="checkbox"
              :value="cookieSetupData.groupId"
              :disabled="cookieSetupData.isEssential"
            >
            <span class="slider absolute pointer left-0 right-0 top-0 bottom-0 round" />
          </label>
        </div>
        <div
          class="cookie-description border info-text"
          v-html="$t(cookieSetupData.description)"
        />
      </div>
      <div class="border-top pt20 info-text">
        {{ informationText }}
        <a
          class="uppercase"
          :href="privacyLink"
        >
          {{ $t('privacy') + '.' }}
        </a>
      </div>
    </div>
    <div
      v-if="gdprCookieSetupData.length > 0"
      class="cookie-buttons flex flex-row mt10"
    >
      <base-button
        class="w-100 mb5 allow-all"
        @click.native="acceptAll"
      >
        {{ $t("Allow All Cookies") }}
      </base-button>
      <base-button
        class="button-primary w-100"
        @click.native="saveSelected"
      >
        {{ $t("Save") }}
      </base-button>
    </div>
  </div>
</template>
<script>
import { onlineHelper } from '@vue-storefront/core/helpers'
import { CookieUserStorage } from 'src/modules/gdpr-cookie-setup/mixin/cookieUserStorage'
import { COOKIE_POLICY_PATH, consentedGroupIds, hasConsentChoice } from 'src/modules/gdpr-cookie-setup/helpers'
import { MODAL_NAME } from 'src/modules/gdpr-cookie-setup'
import ScrollLock from 'theme/mixins/scrollLock'
import i18n from '@vue-storefront/i18n'
import BaseButton from '../../BaseButton'

export default {
  name: 'GdprCookieSetup',
  components: { BaseButton },
  mixins: [CookieUserStorage, ScrollLock],
  props: {
    gdprCookieSetupData: {
      type: Array,
      default: null
    }
  },
  data () {
    return {
      requestingSave: false,
      cookieGroupIds: [],
      privacyLink: COOKIE_POLICY_PATH,
      informationText: i18n.t('We use cookies to improve our services, prepare personalized offers and improve your experience. If you do not accept the optional cookies below, this may affect your user experience. To learn more, read ')
    }
  },
  computed: {
    getCookieSetupData () {
      return this.gdprCookieSetupData
    },
    isOnline (value) {
      return onlineHelper.isOnline
    }
  },
  async mounted () {
    await this.setCookieGroupIdsByChoice()
  },
  methods: {
    /**
     * @return {Promise<void>}
     */
    async acceptAll () {
      await this.persistChoice(this.getAllCookieGroupIds())
    },
    /**
     * @return {Promise<void>}
     */
    async saveSelected () {
      await this.persistChoice(this.cookieGroupIds)
    },
    /**
     * @param {Array} selectedGroupIds
     * @return {Promise<void>}
     */
    async persistChoice (selectedGroupIds) {
      if (this.requestingSave) {
        return
      }
      this.requestingSave = true

      await this.saveConsentChoice(selectedGroupIds)

      this.requestingSave = false

      this.$bus.$emit('modal-hide', MODAL_NAME)
      this.clearAllBodyScrollLocks()
    },
    /**
     * @return {Promise<void>}
     */
    async setCookieGroupIdsByChoice () {
      this.cookieGroupIds = await hasConsentChoice()
        ? await consentedGroupIds()
        : this.getDefaultCheckedGroupIds()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/variables/grid';
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';

.cookie-description {
  margin: 0;
  padding: 0 0 20px 0;
}

.cookie-name {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-text {
  font-size: 18px;
  line-height: 22px;
  color: #6c6c6c;
}

.border-top {
  border-top: 2px solid #eee;
}

label.switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 30px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  background-color: #c4c4c4;
  transition: 0.4s;
}

.slider::before {
  position: absolute;
  content: "";
  height: 22px;
  width: 22px;
  left: 4px;
  bottom: 4px;
  background-color: color(white);
  transition: 0.4s;
}

input:checked + .slider {
  background-color: #4a90e2;
}

input:checked + .slider::before {
  transform: translateX(28px);
}

/* Rounded sliders */
.slider.round {
  border-radius: 56px;
}

.slider.round::before {
  border-radius: 50%;
}

.gdpr-modal-content {
  padding: 20px 40px 40px 40px;
  overflow-y: auto;
  max-height: 65vh;

  /deep/ a {
    font-size: 18px;
    line-height: 22px;
    color: color(primary);

    &:hover,
    &:focus {
      color: color(primary-hover);
    }
  }
}

.gdpr-cookie-group-title {
  font-size: 24px;
  line-height: 29px;
}

.opacity2 {
  opacity: 0.2;
}

.cookie-buttons {
  padding: 0 40px;

  .button:first-of-type {
    margin-bottom: 0;
    margin-right: 20px;
  }

  .button {
    padding: 14px 40px;
    font-weight: bold;
  }

  .allow-all {
    background-color: white;
    color: color(primary);
    border: 2px solid color(primary);
  }
}

.switch {
  margin: 0;
}

@include mobile {
  /deep/ a {
    font-size: 16px;
    line-height: 19px;
  }

  .info-text {
    font-size: 16px;
    line-height: 19px;
  }

  .gdpr-modal-content {
    padding-bottom: 200px;
    max-height: calc(100vh - 200px);
  }

  .cookie-buttons {
    flex-direction: column;
    position: fixed;
    bottom: 0;
    left: 0;
    box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.3);
    z-index: 5;
    width: 100%;
    padding: 20px 15px 30px;
    background-color: white;
    margin-top: 10px;

    .button:first-of-type {
      margin-bottom: 10px;
      margin-right: 0;
    }
  }

  .gdpr-cookie-group-title {
    font-size: 20px;
    line-height: 24px;
  }
}

</style>
